// Lab Partner Portal — TRF Forms page
//
// Mirrors the eDOS HTML mock's `#/forms` view: a kicker, a display
// headline ("Download TRFs."), a lede, and a 2-column grid of
// branch-specific Test Requisition Form download cards. Each card has
// a paper-style PDF icon (with corner-fold), the branch + type/city
// meta, and a "Download" button. Replaces the medical portal's
// TestRequisition view at the `'requisition'` nav target — the lab
// portal's "Test Requisition Form" entry now lands here instead of
// the medical-portal generic form.
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { MP, MONO, DISPLAY } from '../medical/shared';
import { BRANCHES } from './edosData';
import { useUI } from '../../../context/UIContext';

const GREEN_DEEP = '#10151C';
const TEAL_DEEP  = '#008976';

const EdosTrfForms = () => {
  const { showToast } = useUI();

  const handleDownload = (b) => {
    showToast(`TRF for ${b.branch} — wires to /trf/${b.id}.pdf in v2.2.`, 'info');
  };

  return (
    <Box>
      {/* ── Page head ────────────────────────────────────────────── */}
      <Box sx={{ mb: 4 }}>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            bgcolor: MP.tealLight,
            color: GREEN_DEEP,
            fontFamily: MONO,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            px: 1.25,
            py: 0.5,
          }}
        >
          Test Requisition Forms
        </Box>
        <Typography
          component="h1"
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: 32, md: 44 },
            fontWeight: 700,
            letterSpacing: '0.01em',
            textTransform: 'uppercase',
            color: GREEN_DEEP,
            mt: 1.5,
            mb: 1.5,
            lineHeight: 1,
          }}
        >
          Download{' '}
          <Box component="span" sx={{ color: TEAL_DEEP, fontStyle: 'italic' }}>
            TRFs.
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: MP.textMuted, maxWidth: '70ch' }}>
          Branch-specific Test Requisition Forms in printable A4 format. Use
          these for paper-based clinical referrals or to attach to courier
          samples.
        </Typography>
      </Box>

      {/* ── TRF grid ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        {BRANCHES.map((b) => (
          <Box
            key={b.id}
            sx={{
              bgcolor: MP.white,
              border: `1px solid ${MP.border}`,
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              transition: 'all .15s',
              '&:hover': {
                borderColor: MP.teal,
                boxShadow:
                  '0 1px 0 rgba(21,32,26,.04), 0 2px 4px rgba(21,32,26,.04)',
              },
            }}
          >
            {/* Paper-style PDF icon — 48×60 with a folded top-right corner */}
            <Box
              sx={{
                width: 48,
                height: 60,
                bgcolor: MP.bg,
                border: `1px solid ${MP.border}`,
                position: 'relative',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 700,
                color: MP.textMuted,
                letterSpacing: '0.06em',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 14,
                  height: 14,
                  background: `linear-gradient(225deg, ${MP.white} 50%, ${MP.border} 50%)`,
                },
              }}
            >
              PDF
            </Box>

            {/* Branch info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: MP.text,
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {b.branch} – TRF
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 11,
                  color: MP.textMuted,
                  lineHeight: 1.4,
                }}
              >
                {b.type} · {b.city}, {b.state}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => handleDownload(b)}
              sx={{
                borderColor: MP.border,
                color: MP.text,
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                px: 1.5,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: MP.teal,
                  bgcolor: MP.tealLight,
                  color: MP.tealDark,
                },
              }}
            >
              Download
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EdosTrfForms;
